import { useMemo, useState } from 'react';
import { BROKERS, INSTRUMENTS, SWAP_USE_CASES } from '@/mocks/compare';
import { fmtMoney, swapCost, swapFor } from '../calculations';
import { CompareBar, NumberField, SectionHeading, Segmented, SelectField, StatCard } from './ui';

type Dir = 'long' | 'short';

export default function SwapSection() {
  const [symbol, setSymbol] = useState('EURUSD');
  const [dir, setDir] = useState<Dir>('long');
  const [lot, setLot] = useState(1);
  const [days, setDays] = useState(7);

  const inst = INSTRUMENTS.find((i) => i.symbol === symbol) ?? INSTRUMENTS[0];

  const rows = useMemo(
    () =>
      BROKERS.map((b) => ({
        broker: b,
        rate: swapFor(inst, b, dir),
        total: swapCost(inst, b, dir, lot, days),
      })),
    [inst, dir, lot, days],
  );

  const bestTotal = dir === 'long' ? Math.max(...rows.map((r) => r.total)) : Math.max(...rows.map((r) => r.total));
  const worstTotal = Math.min(...rows.map((r) => r.total));
  // For bars, use absolute scale so magnitude is visually comparable
  const maxAbs = Math.max(...rows.map((r) => Math.abs(r.total)), 1);

  return (
    <section id="swap" className="scroll-mt-24 rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <SectionHeading
        eyebrow="So sánh Swap"
        title="So sánh Swap Forex/CFD"
        subtitle="Tính phí swap (lãi suất qua đêm) khi giữ vị thế qua nhiều đêm, và so sánh giữa các nhà môi giới."
      />

      {/* Inputs */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          label="Sản phẩm"
          value={symbol}
          onChange={setSymbol}
          options={INSTRUMENTS.map((i) => ({ value: i.symbol, label: `${i.symbol} — ${i.name}` }))}
        />
        <div>
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Hướng giao dịch</span>
          <Segmented<Dir>
            options={[
              { value: 'long', label: 'Long (Mua)' },
              { value: 'short', label: 'Short (Bán)' },
            ]}
            value={dir}
            onChange={setDir}
          />
        </div>
        <NumberField label="Khối lượng (lot)" value={lot} onChange={setLot} step={0.01} suffix="lot" />
        <NumberField label="Số đêm giữ lệnh" value={days} onChange={setDays} step={1} suffix="đêm" />
      </div>

      {/* Summary stat */}
      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Mức swap tham chiếu" value={`${dir === 'long' ? inst.swapLong : inst.swapShort} USD`} sub="Mỗi lot / đêm" />
        <StatCard
          label="Sàn tốt nhất"
          value={`${bestTotal.toFixed(2)} USD`}
          sub={rows.find((r) => r.total === bestTotal)?.broker.name}
          tone="positive"
        />
        <StatCard
          label="Sàn kém nhất"
          value={`${worstTotal.toFixed(2)} USD`}
          sub={rows.find((r) => r.total === worstTotal)?.broker.name}
          tone="negative"
        />
        <StatCard label="Chênh lệch" value={`${(bestTotal - worstTotal).toFixed(2)} USD`} sub={`Giữa sàn tốt và kém nhất · ${days} đêm`} />
      </div>

      {/* Swap comparison table */}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200/80">
        <table className="w-full min-w-[620px] border-collapse text-sm">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Nhà môi giới</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Swap {dir === 'long' ? 'Long' : 'Short'} (mỗi lot/đêm)</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Tổng swap ({days} đêm)</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Đánh giá</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ broker, rate, total }) => {
              const best = total === bestTotal;
              return (
                <tr key={broker.id} className="border-t border-slate-100 transition-colors hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="flex h-6 w-6 items-center justify-center rounded-md font-display text-[10px] font-bold text-white"
                        style={{ backgroundColor: broker.color }}
                      >
                        {broker.initial}
                      </span>
                      <span className="text-xs font-bold text-slate-700">{broker.name}</span>
                    </div>
                  </td>
                  <td className={`px-4 py-3 font-mono text-xs font-bold tabular-nums ${rate >= 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                    {fmtMoney(rate)}
                  </td>
                  <td className={`px-4 py-3 font-mono text-xs font-bold tabular-nums ${total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {fmtMoney(total)}
                  </td>
                  <td className="px-4 py-3">
                    {best ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                        <i className="ri-thumb-up-line" />
                        Thuận lợi nhất
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cost bar chart */}
      <div className="mt-6 rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
        <h3 className="font-display text-base font-bold text-slate-900">
          Tổng chi phí swap sau {days} đêm ({dir === 'long' ? 'Long' : 'Short'})
        </h3>
        <div className="mt-4 space-y-1.5">
          {rows.map(({ broker, total }) => (
            <CompareBar
              key={broker.id}
              label={broker.name}
              value={Math.abs(total)}
              display={`${total.toFixed(2)} USD`}
              max={maxAbs}
              color={broker.color}
              highlight={total === bestTotal}
            />
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">
          Giá trị dương nghĩa là bạn nhận swap, giá trị âm là phải trả. Swap được tính theo quy ước tham khảo, không bao gồm swap 3 ngày cuối tuần.
        </p>
      </div>

      {/* Use cases */}
      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
        {SWAP_USE_CASES.map((u) => (
          <div key={u.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
              <i className={u.icon} />
            </span>
            <h4 className="mt-3 text-sm font-bold text-slate-800">{u.title}</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{u.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}